// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  HostBinding,
  Input,
  OnChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { map } from 'rxjs';
import { CompanionModel } from 'src/app/api/remote-api/.portal.generated/models';
import { APICallGateComponent } from 'src/app/modules/shared/components/api-call-gate.component';
import { ProcessPlaceholdersPipe } from 'src/app/modules/shared/pipes/process-placeholders.pipe';
import {
  ChangeInfo,
  DeepReadonly,
} from 'src/app/modules/shared/utils/api-call-state.util';
import { ScreenLayoutService } from '../../../shared/services/screen-layout-service/screen-layout.service';
import { ApplicationInsightsService } from '../../services/application-insights/application-insights-service/application-insights.service';
import { SystemOverviewService } from '../../services/systemOverview.service';
import { RefreshDataSourcesButtonComponent } from '../refresh-data-sources-button/refresh-data-sources-button.component';

@Component({
  selector: 'app-companions-panel',
  templateUrl: './companions-panel.component.html',
  styleUrls: ['./companions-panel.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    APICallGateComponent,
    ProcessPlaceholdersPipe,
    CommonModule,
    RefreshDataSourcesButtonComponent
],
})
export class CompanionsPanelComponent implements OnChanges {
  @Input({ required: true }) systemMaterialNumber!: number;
  @Input({ required: true }) systemSerialNumber!: string;
  @Input({ required: true }) modalityId!: number;

  @HostBinding('attr.hidden') hidden: '' | null = null;

  changedApps = new Set<string>();

  companions = this.systemOverviewService.companions;
  hidden$ = this.companions.lastSuccessfulData$.pipe(
    map((data) => !data?.length),
  );
  systemDetails$ = this.systemOverviewService.systemDetails.lastSuccessfulData$;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected screenLayoutService: ScreenLayoutService,
    protected systemOverviewService: SystemOverviewService,
    protected appInsightsService: ApplicationInsightsService,
  ) {
    this.hidden$
      .pipe(takeUntilDestroyed())
      .subscribe((hidden) => (this.hidden = hidden ? '' : null));
  }

  open(url: string, companion?: CompanionModel): void {
    // in case of host-absolute urls we use our internal routing
    if (url.match(/^\/portal/)) {
      this.router.navigateByUrl(url.replace(/^\/portal/, ''));
      if (this.isDicomCompanionAndEnabled(companion)) {
        this.trackDicomCompanionClickedEvent();
      } else if (this.isDetectorMonitorAndEnabled(companion)) {
        this.trackDetectorMonitorClickedEvent();
      }
      return;
    }
    // in case of relative urls we need to prefix them with our origin
    // backend should fix this
    if (!url.match(/^http(s):/)) {
      url = window.origin + (url.startsWith('/') ? url : '/' + url);
    }
    window.open(url);
  }

  private isCompanionOfTypeAndEnabled(
    titleKeyword: string,
    companion?: CompanionModel,
  ): boolean {
    if (companion) {
      const title = (companion.title ?? '').toLowerCase();
      return (
        title.includes(titleKeyword) && !this.isCompanionDisabled(companion)
      );
    }
    return false;
  }

  private isDicomCompanionAndEnabled(companion?: CompanionModel): boolean {
    return this.isCompanionOfTypeAndEnabled('dicom companion', companion);
  }

  private isDetectorMonitorAndEnabled(companion?: CompanionModel): boolean {
    return this.isCompanionOfTypeAndEnabled('detector companion', companion);
  }

  private trackDicomCompanionClickedEvent(): void {
    this.appInsightsService.trackComponentUsageEvent('DICOMCompanionPage');
  }

  private trackDetectorMonitorClickedEvent(): void {
    this.appInsightsService.trackComponentUsageEvent('DetectorMonitorPage');
  }

  ngOnChanges(): void {
    this.companions.request({
      systemMaterialNumber: this.systemMaterialNumber,
      systemSerialNumber: this.systemSerialNumber,
      modalityId: this.modalityId,
    });
  }

  indicateRefresh(d: DeepReadonly<ChangeInfo<CompanionModel[]>[]>): void {
    // look for changed components
    {
      const companions = d.find((item) =>
        /\/companions\//.exec(item.url),
      ) as ChangeInfo<CompanionModel[]>;
      if (companions && !companions.same) {
        // find changed components
        for (const companion of companions.new) {
          const prev = companions.old.find((c) => c.link === companion.link);
          if (!prev || JSON.stringify(prev) !== JSON.stringify(companion)) {
            this.changedApps.add(companion.title!);
          }
        }
      }
    }
  }

  getTimeFromNow(dateTime: string): string {
    if (dateTime === undefined || dateTime === null || dateTime === '') {
      return 'unknown';
    }
    try {
      const givenTime = moment.utc(dateTime);
      if (!givenTime.isValid()) {
        return 'unknown';
      }
      const nowUtc = moment.utc();
      return givenTime.from(nowUtc);
    } catch {
      return 'unknown';
    }
  }

  isCompanionDisabled(companion: CompanionModel): boolean {
    return !companion.lastUpdate;
  }

  getCompanionId(companion: CompanionModel): string {
    return (
      (companion.title ?? '').replace(' ', '-').toLowerCase() + '-timestamp'
    );
  }
}
